import {
  OFFICE_TOOLS_GROUP_ID,
  getShortcutGroupCapabilities,
  isOfficeToolsGroup
} from 'modules/desktop/shortcutGroups'
import { ShortcutGroup } from 'shared/types/desktop-grid'
import { create } from 'zustand'

type ShortcutGroupStore = {
  groups: ShortcutGroup[]
  hydrateGroups: (groups: ShortcutGroup[]) => void
  clearGroups: () => void
  pruneMembers: (availableMemberIds: string[]) => void
  provisionOfficeToolsGroup: (title: string, memberIds: string[]) => void
  createGroup: (memberId: string) => string
  renameGroup: (groupId: string, title: string) => void
  addMembers: (groupId: string, memberIds: string[]) => void
  removeMembers: (memberIds: string[]) => void
  deleteGroup: (groupId: string) => void
}

const makeGroupId = () =>
  `shortcut-group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const normalizeTitle = (title: string) => title.trim().toLocaleLowerCase()

const uniqueTitle = (title: string, groups: ShortcutGroup[]) => {
  const baseTitle = title.trim() || 'New group'
  const usedTitles = new Set(groups.map(group => normalizeTitle(group.title)))
  if (!usedTitles.has(normalizeTitle(baseTitle))) return baseTitle

  let suffix = 2
  let candidate = `${baseTitle} ${suffix}`
  while (usedTitles.has(normalizeTitle(candidate))) {
    suffix += 1
    candidate = `${baseTitle} ${suffix}`
  }
  return candidate
}

const ensureUniqueTitles = (groups: ShortcutGroup[]) => {
  // The system group owns its localized title. Reserve that title before
  // normal groups are normalized so a user-created duplicate is suffixed,
  // never the other way around.
  const systemGroup = groups.find(
    group => !getShortcutGroupCapabilities(group).canRename
  )
  const ordered = systemGroup
    ? [systemGroup, ...groups.filter(group => group.id !== systemGroup.id)]
    : groups
  const normalized: ShortcutGroup[] = []
  ordered.forEach(group => {
    if (!getShortcutGroupCapabilities(group).canRename) {
      normalized.push(group)
      return
    }
    const title = uniqueTitle(group.title, normalized)
    normalized.push(title === group.title ? group : { ...group, title })
  })

  const byId = new Map(normalized.map(group => [group.id, group]))
  const result = groups.map(group => byId.get(group.id) ?? group)
  return result.every((group, index) => group === groups[index])
    ? groups
    : result
}

export const useShortcutGroupStore = create<ShortcutGroupStore>(set => ({
  groups: [],

  hydrateGroups: groups =>
    set({
      groups: ensureUniqueTitles(groups)
    }),

  clearGroups: () =>
    set(state => (state.groups.length === 0 ? state : { groups: [] })),

  pruneMembers: availableMemberIds =>
    set(state => {
      const available = new Set(availableMemberIds)
      const nextGroups = state.groups
        .map(group => {
          const memberIds = group.memberIds.filter(id => available.has(id))
          return memberIds.length === group.memberIds.length
            ? group
            : { ...group, memberIds }
        })
        // canDelete protects system groups from user actions. Reconciliation
        // is driven by the files API, so even a protected group must disappear
        // once none of its members exist on the desktop anymore.
        .filter(group => group.memberIds.length > 0)
      return nextGroups.every(
        (group, index) => group === state.groups[index]
      ) && nextGroups.length === state.groups.length
        ? state
        : { groups: nextGroups }
    }),

  provisionOfficeToolsGroup: (title, memberIds) =>
    set(state => {
      const existing = state.groups.find(group => isOfficeToolsGroup(group))
      const memberSet = new Set(memberIds)
      let systemGroupSeen = false
      const normalizedGroups = state.groups
        .flatMap(group => {
          if (!isOfficeToolsGroup(group)) {
            return [
              {
                ...group,
                memberIds: group.memberIds.filter(id => !memberSet.has(id))
              }
            ]
          }
          if (systemGroupSeen) return []
          systemGroupSeen = true
          return [
            {
              ...group,
              id: OFFICE_TOOLS_GROUP_ID,
              title,
              memberIds: [...memberIds],
              kind: 'office-tools' as const
            }
          ]
        })
        .filter(
          group =>
            !getShortcutGroupCapabilities(group).canDelete ||
            group.memberIds.length > 0
        )

      if (!existing) {
        normalizedGroups.push({
          id: OFFICE_TOOLS_GROUP_ID,
          title,
          memberIds: [...memberIds],
          kind: 'office-tools',
          createdAt: new Date()
        })
      }

      const groups = ensureUniqueTitles(normalizedGroups)
      return groups === state.groups ? state : { groups }
    }),

  createGroup: memberId => {
    const id = makeGroupId()
    set(state => {
      const title = uniqueTitle('New group', state.groups)
      return {
        groups: [
          ...state.groups,
          {
            id,
            title,
            memberIds: [memberId],
            createdAt: new Date(),
            kind: 'user'
          }
        ]
      }
    })
    return id
  },

  renameGroup: (groupId, title) =>
    set(state => {
      if (!getShortcutGroupCapabilities(groupId).canRename) return state
      const otherGroups = state.groups.filter(group => group.id !== groupId)
      const nextTitle = uniqueTitle(title, otherGroups)
      return {
        groups: state.groups.map(group =>
          group.id === groupId ? { ...group, title: nextTitle } : group
        )
      }
    }),

  addMembers: (groupId, memberIds) =>
    set(state => {
      if (!getShortcutGroupCapabilities(groupId).canAcceptMembers) return state
      const lockedMemberIds = new Set(
        state.groups
          .filter(group => !getShortcutGroupCapabilities(group).canMoveMembers)
          .flatMap(group => group.memberIds)
      )
      const movableMemberIds = memberIds.filter(
        memberId => !lockedMemberIds.has(memberId)
      )
      const ids = new Set(movableMemberIds)
      return {
        groups: state.groups
          .map(group =>
            !getShortcutGroupCapabilities(group).canMoveMembers
              ? group
              : group.id === groupId
                ? {
                    ...group,
                    memberIds: [
                      ...group.memberIds,
                      ...movableMemberIds.filter(
                        id => !group.memberIds.includes(id)
                      )
                    ]
                  }
                : {
                    ...group,
                    memberIds: group.memberIds.filter(id => !ids.has(id))
                  }
          )
          .filter(
            group =>
              !getShortcutGroupCapabilities(group).canDelete ||
              group.memberIds.length > 0
          )
      }
    }),

  removeMembers: memberIds =>
    set(state => ({
      groups: state.groups
        .map(group => ({
          ...group,
          memberIds: !getShortcutGroupCapabilities(group).canRemoveMembers
            ? group.memberIds
            : group.memberIds.filter(id => !memberIds.includes(id))
        }))
        .filter(
          group =>
            !getShortcutGroupCapabilities(group).canDelete ||
            group.memberIds.length > 0
        )
    })),

  deleteGroup: groupId =>
    set(state => ({
      groups: !getShortcutGroupCapabilities(groupId).canDelete
        ? state.groups
        : state.groups.filter(group => group.id !== groupId)
    }))
}))
